// ReferrerEarningsCard.tsx
import React, { useState } from "react";
import { ReferrerEarningsCardProps } from "../types/revenue.types";
import RevenueService from "../../../services/revenueService";

export const ReferrerEarningsCard: React.FC<ReferrerEarningsCardProps> = ({
  referrer,
  colorTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        marginBottom: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
      }}
    >
      {/* Referrer Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: colorTheme.gray900,
              margin: "0 0 4px 0",
            }}
          >
            🤝 {referrer.referrer_name}
          </h3>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span style={{ color: colorTheme.gray500 }}>
              {referrer.transactions_count} referrals
            </span>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#8b5cf6",
            }}
          >
            {RevenueService.formatCurrency(referrer.total_earned)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colorTheme.gray500,
              marginTop: "4px",
            }}
          >
            Total earnings
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "16px",
          padding: "16px",
          background: colorTheme.gray50,
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#8b5cf6",
            }}
          >
            {RevenueService.formatCurrency(referrer.avg_per_referral)}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Avg per Referral
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.success,
            }}
          >
            {referrer.transactions_count}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Total Referrals
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.warning,
            }}
          >
            {referrer.services_referred.length}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Service Types
          </div>
        </div>
      </div>

      {/* Services Breakdown Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          padding: "12px",
          background: expanded ? "#8b5cf6" : colorTheme.gray100,
          color: expanded ? "white" : colorTheme.gray700,
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          transition: "all 0.3s ease",
        }}
      >
        {expanded ? "Hide" : "Show"} Referral Breakdown {expanded ? "▲" : "▼"}
      </button>

      {/* Expanded Services Details */}
      {expanded && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: colorTheme.gray50,
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Services Referred
          </h4>
          {referrer.services_referred.map((service: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index < referrer.services_referred.length - 1
                    ? `1px solid ${colorTheme.gray200}`
                    : "none",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: colorTheme.gray900,
                  }}
                >
                  {service.service_name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: colorTheme.gray600,
                  }}
                >
                  {service.count} referrals •{" "}
                  {RevenueService.formatCurrency(
                    service.earned / service.count,
                  )}{" "}
                  per referral
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#8b5cf6",
                  }}
                >
                  {RevenueService.formatCurrency(service.earned)}
                </div>
              </div>
            </div>
          ))}

          {/* Recent Referrals */}
          {referrer.recent_referrals.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Recent Referrals
              </h4>
              {referrer.recent_referrals
                .slice(0, 3)
                .map((referral: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{ color: colorTheme.gray900, fontWeight: "500" }}
                      >
                        {referral.patient_name}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, margin: "0 8px" }}
                      >
                        •
                      </span>
                      <span style={{ color: colorTheme.gray600 }}>
                        {referral.service}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: "#8b5cf6", fontWeight: "600" }}>
                        {RevenueService.formatCurrency(referral.earned)}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, marginLeft: "8px" }}
                      >
                        {new Date(referral.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: "#8b5cf6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          💰 Process Payment
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📊 View Details
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.gray200,
            color: colorTheme.gray700,
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📧 Send Report
        </button>
      </div>
    </div>
  );
};
